import { isPermissionError } from '../../../handlers/globalErrorHandler'
import WbsItemApi, { WbsItemBasic } from '../../../lib/functions/wbsItem'

// 応答本文では同じ WBS アイテムへの言及が複数回・複数メッセージにまたがって出現しうる
// （例: 同じチケットを繰り返し参照しながら説明する）。WbsItemLinkChip は本文中に独立して
// いくつも並ぶコンポーネントだが、そのためだけに Context/Redux を新設するほどではないため、
// モジュールスコープの単純なキャッシュで「同じコードは1回しか取得しない」を保証する
// （CLAUDE.md §7: 画面内はおろか他画面にも関係しない、取得結果の使い回しだけの用途）。
// タブを閉じる・リロードすると消えるが、頻繁に変わる値ではないため再取得のコストは小さい。
//
// 取得に失敗したときの表示は理由を問わず同じ（チップをやめて本文のプレーンテキストにする）だが、
// 失敗を **キャッシュするかどうか** は理由によって変える。404（存在しないコード = モデルの幻覚）と
// 403（参照権限なし）は何度引いても結論が変わらないのでキャッシュして再取得しない。一方
// 500 やネットワーク断は一時的な失敗であり、キャッシュに残すとページをリロードするまで二度と
// 表示できなくなるため、キャッシュせず次の取得機会にやり直せるようにする。
const cache = new Map<string, WbsItemBasic | null>()
const inFlight = new Map<string, Promise<WbsItemBasic | null>>()

/**
 * 再取得しても結論が変わらない失敗か。true なら失敗をキャッシュしてよい。
 *
 * 参照権限なし（`UNAUTHORIZED` / `UNAUTHORIZED_SCOPE`）と 404（存在しない）が該当する。
 * それ以外（サーバエラー・ネットワーク断など）は一時的な失敗として扱い、キャッシュしない。
 */
export const isPermanentLookupFailure = (error: unknown): boolean => {
  if (isPermissionError(error)) {
    return true
  }
  return (error as { status?: number } | null)?.status === 404
}

// 同期的にキャッシュだけを覗く。initial state 用。
// undefined: 未取得（呼び出し側が loadWbsItemBasic を呼ぶ必要がある）
// null: 取得済みだが利用不可（存在しない or 参照権限なし）
// それ以外: 取得済みの実体
export const peekCachedWbsItemBasic = (
  code: string
): WbsItemBasic | null | undefined => cache.get(code)

export const loadWbsItemBasic = (
  code: string
): Promise<WbsItemBasic | null> => {
  if (cache.has(code)) {
    return Promise.resolve(cache.get(code) ?? null)
  }
  const pending = inFlight.get(code)
  if (pending) {
    return pending
  }
  const request = WbsItemApi.getBasicByCode(code)
    .then(response => {
      const basic = response.json as WbsItemBasic
      cache.set(code, basic)
      return basic
    })
    .catch(error => {
      // 本文中の一要素が引けなかっただけであり、会話全体を止めたり全画面トーストを出したり
      // する話ではない。globalErrorHandler は unhandledrejection を拾って全画面トーストを
      // 出す作りのため、ここで確実に catch して伝播を止める（再throwしない）。原因の切り分けが
      // できるよう console にだけ残す。
      console.warn('Failed to fetch wbs item basic for a chat link chip.', {
        code,
        error,
      })
      if (isPermanentLookupFailure(error)) {
        cache.set(code, null)
      }
      return null
    })
    .finally(() => {
      inFlight.delete(code)
    })
  inFlight.set(code, request)
  return request
}
